// Trigger deployment update: Webpack build imports & Docker env args configured
import { useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { ConfigProvider } from "antd";
import { createBrowserRouter, Navigate, RouterProvider, useParams } from "react-router-dom";
import AppLayout, { appLoader } from "./modules/layout";
import SpaceSettingsPage from "./modules/space";
import LoginPage from "./modules/login";
import { GoogleOAuthProvider } from "@react-oauth/google";
import SpacesPage from "./modules/space/SpacesPage";
import OrganisationChartPage from "./modules/organisationchart";
import EmployeeMaster from "./modules/organisationchart/EmployeeMaster";
import NotificationCenterList from "./modules/notification-center/list";
import NotificationCenterEdit from "./modules/notification-center/edit";
import DocumentsList from "./modules/space/DocumentsList";
import AllSpacesPage from "./modules/space/AllSpacesPage";
import AdminDashboard from "./modules/adminscreens/AdminDashboard";
import CategoryMaster from "./modules/organisationchart/CategoryMaster";
import RolesAndEntitlements from "./modules/organisationchart/RolesAndEntitlements";
import SpaceReporting from "./modules/reporting/Index";
import { fetchSpaceImages, fetchUserGroup, fetchUserInfo, fetchSpaceForUserGroups, fetchUserHubInfo, fetchSpaceUsers, fetchPlanLimits } from "./redux/actions/userAction";
import CtrlHandler from "./CtrlHandler";
// import { useTranslation } from "react-i18next";
import { useTranslation } from "react-i18next";

import "./App.css";
import "./style/common.css"
import "./style/customAnt.css"
import Canvas from "./modules/canvas";
import FormEditor from "./modules/form-editor/Index";
import UserLogin from "./modules/login/UserLogin";
import UserAndRoleManagement from "./modules/adminscreens/userAndRole";
import AdminConfig from "./modules/adminscreens/AdminConfig";
import ActivityMonitoring from "./modules/adminscreens/activityMonitoring/ActivityMonitoring";
import AdministratorControls from "./modules/adminscreens/AdministratorControls";
import DataTables from "./modules/adminscreens/masterData/DataTables";
import AllMasterDataPage from "./modules/adminscreens/masterData/AllMasterDataPage";
import EstimationEngine from "./modules/estimationEngine";
import Requests from "./modules/requests";
import GeneralSettings from "./modules/userAccount/GeneralSettings";
import Configuration from "./modules/adminscreens/configurations/Index";
import EstimationSummary from "./modules/estimationEngine/estimationSummary";
import PublicFormPage from "./modules/public-form/PublicFormPage";
import NotFound from "./components/NotFound";
import LoaderOverlay from "./components/Loader/LoaderOverlay";
import PlanLimitBanner from "./components/PlanLimitBanner";
import AdminRoute from "./components/AdminRoute";
import ForgotPassword from "./modules/login/ForgotPassword";
import CreatePassword from "./modules/login/CreatePassword";
import { TenantProvider } from "./context/TenantContext.jsx";
import TenantRouteWrapper from "./components/TenantRouteWrapper.jsx";
import SearchResult from "./modules/search-result";



const GOOGLE_OAUTH_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";

// Define app children routes - these will be used for both default and tenant-prefixed routes
const appChildrenRoutes = [
          {
            path: "user/settings",
            element: <GeneralSettings />
          },
          {
            path: "dashboard",
            element: <SpacesPage />,
          },
          {
            path: "space/:spaceId/space-settings",
            element: <SpaceSettingsPage />,
          },
          {
            path: "space/:spaceId/notification-center",
            element: <NotificationCenterList />,
          },
          {
            path: "space/:spaceId/notification-center/:notificationId",
            element: <NotificationCenterEdit />,
          },
          {
            path: "space/:spaceId/reporting",
            element: <SpaceReporting />,
          },
          {
            path: "space/:spaceId/documents",
            element: <DocumentsList />
          },
          {
            path: "space/all-spaces",
            element: <AllSpacesPage />
          },
          {
            path: "space/:spaceId/workflow",
            element: <Canvas />
          },
          {
            path: "space/:spaceId/form-editor",
            element: <FormEditor />
          },
          {
            path: "space/:spaceId/:workflowId/:nodeId/form-editor/:formId",
            element: <FormEditor />
          },
          {
            path: "space/:spaceId/:mode/:workflowId/:requestId",
            element: <Requests />
          },
          {
            path: "space/:spaceId/estimation-engine",
            element: <EstimationEngine />
          },
          {
            path: "space/:spaceId/estimation-summary",
            element: <EstimationSummary />
          },
          {
            path: "search",
            element: <SearchResult />
          },
          // Admin-only routes
          {
            element: <AdminRoute />,
            children: [
              // {
              //   path: "admin/dashboard",
              //   element: <AdminDashboard />
              // },
              {
                path: "admin/config",
                element: <AdminConfig />
              },
              {
                path: "admin/controls",
                element: <AdministratorControls />
              },
              {
                path: "admin/user-role",
                element: <UserAndRoleManagement />
              },
              {
                path: "admin/configuration",
                element: <Configuration />
              },
              {
                path: "admin/configuration/organisation-structure",
                element: <OrganisationChartPage />,
              },
              {
                path: "admin/configuration/workflow-configuration",
                element: <Configuration />
              },
              {
                path: "admin/master-data/all",
                element: <AllMasterDataPage />
              },
              {
                path: "admin/master-data/tables/:tableId",
                element: <DataTables />
              },
              {
                path: "admin/create-master/form-editor/:tableId",
                element: <FormEditor />
              },
              {
                path: "organisation-config/employee-master",
                element: <EmployeeMaster title="Employee Master" />,
              },
              {
                path: "organisation-config/category-master",
                element: <CategoryMaster title="Category Master" />,
              },
              {
                path: "organisation-config/roles-and-entitlements",
                element: <RolesAndEntitlements />,
              },
              {
                path: "admin/activity-monitoring",
                element: <ActivityMonitoring />
              }
            ],
          },
];

// Router must be created OUTSIDE the component to prevent recreation on every render
const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: <Navigate to="/login" replace />,
      },
      {
        path: "login",
        element: <LoginPage />,
        errorElement: <NotFound />,
      },
{
        path: "forgot-password",
        element: <ForgotPassword />,
        errorElement: <NotFound />,
      },
      {
        path: "create-password",
        element: <CreatePassword />,
        errorElement: <NotFound />,
      },
      {
        path: "custom-login",
        element: <UserLogin />
      },
      {
        path: "public/form/:shareToken",
        element: <PublicFormPage />
      },
      // Default routes (without tenant prefix)
      {
        path: "app",
        element: <AppLayout />,
        errorElement: <NotFound />,
        children: appChildrenRoutes,
      },
      // Tenant-prefixed routes (wrapped with TenantRouteWrapper for validation)
      {
        path: ":tenantName",
        element: <TenantRouteWrapper />,
        errorElement: <NotFound />,
        children: [
          {
            path: "login",
            element: <LoginPage />,
            errorElement: <NotFound />,
          },
{
            path: "forgot-password",
            element: <ForgotPassword />,
            errorElement: <NotFound />,
          },
          {
            path: "create-password",
            element: <CreatePassword />,
            errorElement: <NotFound />,
          },
          {
            path: "custom-login",
            element: <UserLogin />
          },
          {
            path: "app",
            element: <AppLayout />,
            errorElement: <NotFound />,
            children: appChildrenRoutes,
          },
        ],
      },
    ],
  },
]);

const App = () => {
  const dispatch = useDispatch();
  const { userInfo, userGroups } = useSelector((state) => state.user);
  const { i18n } = useTranslation();


  useEffect(() => {
    const handleStorage = () => {
      let userLoggedIn = localStorage.getItem("isLoggedIn") || false;
      if (userLoggedIn) {
        dispatch(fetchUserInfo());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [dispatch]);

  useEffect(() => {
    if (userInfo && !userInfo.userId && localStorage.getItem("userEmail")) {
      dispatch(fetchUserInfo());
    }
  }, [userInfo, dispatch]);

  useEffect(() => {
    if (userInfo.defaultLanguage) {
      localStorage.setItem("userLanguage", userInfo.defaultLanguage)
      // A public form link carries its own language and anyone may be signed in
      // while opening one. Applying the profile language here would overwrite the
      // link's locale as soon as userInfo loads, leaving the surrounding UI
      // (verification modal, submit/draft buttons) in English on a translated form.
      if (!window.location.pathname.includes("/public/form/")) {
        i18n.changeLanguage(userInfo.defaultLanguage)
      }
    }
  }, [userInfo.defaultLanguage])

  useEffect(() => {
    if (userInfo.email) {
      dispatch(fetchUserGroup(userInfo.email, userInfo.userId));
    }
  }, [userInfo, dispatch]);

useEffect(() => {
    if (userGroups?.length > 0 && userInfo.userId) {
      dispatch(fetchSpaceForUserGroups(userGroups, userInfo.userId));
    }
  }, [userGroups, userInfo, dispatch]);

  // Fetch plan limits when user is logged in and has company_name
  useEffect(() => {
    if (userInfo?.company_name && userInfo?.isAdmin) {
      dispatch(fetchPlanLimits(userInfo.company_name));
    }
  }, [userInfo?.company_name, userInfo?.isAdmin, dispatch]);

return (
    <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
      <ConfigProvider direction="ltr">
        <TenantProvider>
          <PlanLimitBanner />
          <LoaderOverlay />
          <RouterProvider router={router} />
        </TenantProvider>
      </ConfigProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
